import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import {
  Calendar,
  Plus,
  Building2,
  Users,
  Car,
  UserCircle,
  DollarSign,
  FileText,
  LogOut,
  Menu,
  X,
  Settings,
  BarChart3,
  Briefcase,
  Bus
} from 'lucide-react';

const DOSOFT_LOGO = "https://customer-assets.emergentagent.com/job_30a67381-0193-4972-adc8-4683263d2f57/artifacts/0sxu9ue3_Logo%20DoSoft.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [empresaLogo, setEmpresaLogo] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  const isAdmin = user?.role === 'admin';

  // Cargar logo de la empresa del usuario
  useEffect(() => {
    const loadEmpresaLogo = async () => {
      if (user?.empresa_id) {
        try {
          const logoUrl = `${API_URL}/api/configuracion/logo?empresa_id=${user.empresa_id}&t=${Date.now()}`;
          const response = await fetch(logoUrl);
          if (response.ok) {
            // Convertir a blob y luego a URL
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setEmpresaLogo(objectUrl);
          } else {
            setEmpresaLogo(null);
          }
        } catch (error) {
          console.log('No hay logo de empresa, usando logo por defecto');
          setEmpresaLogo(null);
        }
      }
      setLogoLoaded(true);
    };
    
    loadEmpresaLogo();
    
    // Cleanup
    return () => {
      if (empresaLogo && empresaLogo.startsWith('blob:')) {
        URL.revokeObjectURL(empresaLogo);
      }
    };
  }, [user?.empresa_id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = isAdmin ? [
    { icon: Calendar, label: 'Calendario', path: '/admin/calendario' },
    { icon: Plus, label: 'Solicitar Viaje', path: '/admin/solicitar' },
    { icon: Briefcase, label: 'Clientes', path: '/admin/clientes' },
    { icon: Users, label: 'Usuarios', path: '/admin/usuarios' },
    { icon: Car, label: 'Vehículos', path: '/admin/vehiculos' },
    { icon: UserCircle, label: 'Conductores', path: '/admin/conductores' },
    { icon: DollarSign, label: 'Tarifas', path: '/admin/tarifas' },
    { icon: Bus, label: 'Tipos de Viaje', path: '/admin/tipos-viaje' },
    { icon: FileText, label: 'Reportes', path: '/admin/reportes' },
    { icon: BarChart3, label: 'Gestión', path: '/admin/gestion' },
    { icon: Settings, label: 'Configuración', path: '/admin/configuracion' },
  ] : [
    { icon: Calendar, label: 'Calendario', path: '/cliente/calendario' },
    { icon: Plus, label: 'Solicitar Viaje', path: '/cliente/solicitar' },
    { icon: Building2, label: 'Centros de Costo', path: '/cliente/centros-costo' },
    { icon: Users, label: 'Mis Usuarios', path: '/cliente/usuarios' },
    { icon: FileText, label: 'Reportes', path: '/cliente/reportes' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-[#4A4A4A] text-white transition-all duration-300 z-30 ${
        sidebarOpen ? 'w-64' : 'w-0'
      } overflow-hidden`}>
        {/* Logo container - altura igual al header */}
        <div className="h-[73px] flex items-center justify-center bg-white border-b border-slate-700 px-4">
          <img 
            src={empresaLogo || DOSOFT_LOGO}
            alt={empresaLogo ? "Logo Empresa" : "DoSoft"}
            className="h-12 w-auto max-w-[200px] object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = DOSOFT_LOGO;
            }}
          />
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive
                    ? 'bg-[#6BA3B7] text-white'
                    : 'text-slate-300 hover:bg-slate-600 hover:text-white'
                }`}
                data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} strokeWidth={1.5} />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-600">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-600"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut size={20} strokeWidth={1.5} className="mr-3" />
            <span>Cerrar Sesión</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        sidebarOpen ? 'ml-64' : 'ml-0'
      }`}>
        {/* Header - altura fija para alinear con el logo */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-200 h-[73px] flex items-center px-6">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
              <h1 className="text-xl font-bold font-['Manrope'] text-gray-900">
                {isAdmin ? 'Panel de Administrador' : 'Panel de Cliente'}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.nombre}</p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'client' 
                    ? (user?.cliente_nombre || user?.empresa_nombre || 'Cliente')
                    : (user?.empresa_nombre || 'Administrador')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
                {user?.nombre?.charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 min-h-[calc(100vh-73px-48px)]">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="h-12 flex items-center justify-center border-t border-gray-200 bg-gray-50 text-sm text-gray-500">
          © {new Date().getFullYear()} DoSoft
        </footer>
      </div>
    </div>
  );
};